import { useId } from 'react';
import { Badge } from '@/components/Badge';
import ActionButton from '@/components/Button/ActionButton';
import Input from '@/components/Input/Input';
import { HStack, VStack } from '@/components/Layout';
import { SelectField } from '@/components/Select/SelectField';
import { Text } from '@/components/Typography';
import { Separator } from '@/components/ui/separator';
import type { CommuteRouteListItem } from '@/features/section-attribute/types';
import { useCommonTranslation } from '@/hooks/useCommonTranslation';
import { defaultNS, useTranslation } from '@/lib/i18n';
import { CFormItemBase, type CFormItemProps, buildCFormItem } from '../CFormItem';
type RouteMethod = CommuteRouteListItem['methods'][number];
type RouteFare = CommuteRouteListItem['fares'][number];
type CommutingMethod = NonNullable<RouteMethod['commutingMethod']>;
type FareType = NonNullable<RouteFare['fareType']>;
type Currency = NonNullable<RouteFare['currency']>;
type Props = CFormItemProps & {
  value: CommuteRouteListItem[];
  onChange: (value: CommuteRouteListItem[]) => void;
};
function MethodBadges({
  methods,
  getLabel
}: {
  methods: RouteMethod[];
  getLabel: (method: CommutingMethod) => string;
}) {
  return <HStack className="gap-1 flex-wrap">
      {methods.map((m, i) => <Badge key={`${m.commutingMethod}-${i}`} variant={m.isPrimary ? 'secondary' : 'neutralMuted'}>
          {m.commutingMethod ? getLabel(m.commutingMethod) : ''}
        </Badge>)}
    </HStack>;
}
function FareDisplay({
  fares,
  getLabel
}: {
  fares: RouteFare[];
  getLabel: (type: FareType) => string;
}) {
  if (fares.length === 0) return null;
  return <HStack className="gap-4 flex-wrap">
      {fares.map((f, i) => <HStack key={`fare-${i}`} className="gap-1 items-baseline">
          <Text variant="labelSmallReg" className="text-neutral-muted-on-fill">
            {f.fareTypeLabel ?? (f.fareType ? getLabel(f.fareType) : '')}:
          </Text>
          <Text variant="p">
            {f.fareAmount} {f.currency}
          </Text>
        </HStack>)}
    </HStack>;
}
const EMPTY_ROUTE: CommuteRouteListItem = {
  methods: [{
    commutingMethod: 'TRAIN',
    isPrimary: true
  }],
  routeDetail: '',
  distance: '',
  duration: null,
  fares: []
};
export const CFormCommuteRouteListItem = buildCFormItem((props: Props) => {
  const tc = useCommonTranslation();
  const {
    t
  } = useTranslation(defaultNS, {
    keyPrefix: 'features.cForm.commuteRouteItem'
  });
  const idPrefix = useId();
  const routes = props.value ?? [];
  const commutingMethodLabel = (method: CommutingMethod) => t(`commutingMethod.${method}`);
  const COMMUTING_METHOD_OPTIONS = (['TRAIN', 'BUS', 'BIKE', 'WALK', 'CAR', 'COMPANY_BUS'] as CommutingMethod[]).map(key => ({
    id: key,
    label: commutingMethodLabel(key)
  }));
  const fareTypeLabel = (type: FareType) => t(`fareType.${type}`);
  const FARE_TYPE_OPTIONS = (['ONE_WAY', 'MONTHLY', 'QUARTERLY', 'HALF_YEARLY'] as FareType[]).map(key => ({
    id: key,
    label: fareTypeLabel(key)
  }));
  const currencyLabel = (code: Currency) => t(`currency.${code}`);
  const CURRENCY_OPTIONS = (['JPY', 'USD', 'IDR', 'THB', 'VND', 'CNY'] as Currency[]).map(key => ({
    id: key,
    label: currencyLabel(key)
  }));
  const handleRouteChange = (index: number, patch: Partial<CommuteRouteListItem>) => {
    const next = routes.map((r, i) => i === index ? {
      ...r,
      ...patch
    } : r);
    props.onChange(next);
  };
  const handleAddRoute = () => {
    props.onChange([...routes, structuredClone(EMPTY_ROUTE)]);
  };
  const handleRemoveRoute = (index: number) => {
    props.onChange(routes.filter((_, i) => i !== index));
  };
  const handleAddMethod = (routeIndex: number) => {
    const route = routes[routeIndex];
    handleRouteChange(routeIndex, {
      methods: [...route.methods, {
        commutingMethod: 'TRAIN',
        isPrimary: false
      }]
    });
  };
  const handleRemoveMethod = (routeIndex: number, methodIndex: number) => {
    const route = routes[routeIndex];
    handleRouteChange(routeIndex, {
      methods: route.methods.filter((_, i) => i !== methodIndex)
    });
  };
  const handleMethodChange = (routeIndex: number, methodIndex: number, method: CommutingMethod) => {
    const route = routes[routeIndex];
    const newMethods = route.methods.map((m, i) => i === methodIndex ? {
      ...m,
      commutingMethod: method
    } : m);
    handleRouteChange(routeIndex, {
      methods: newMethods
    });
  };
  const handleAddFare = (routeIndex: number) => {
    const route = routes[routeIndex];
    handleRouteChange(routeIndex, {
      fares: [...route.fares, {
        fareType: 'ONE_WAY',
        fareTypeLabel: null,
        currency: 'JPY',
        fareAmount: ''
      }]
    });
  };
  const handleRemoveFare = (routeIndex: number, fareIndex: number) => {
    const route = routes[routeIndex];
    handleRouteChange(routeIndex, {
      fares: route.fares.filter((_, i) => i !== fareIndex)
    });
  };
  const handleFareChange = (routeIndex: number, fareIndex: number, patch: Partial<RouteFare>) => {
    const route = routes[routeIndex];
    const newFares = route.fares.map((f, i) => i === fareIndex ? {
      ...f,
      ...patch
    } : f);
    handleRouteChange(routeIndex, {
      fares: newFares
    });
  };
  const renderReadMode = () => {
    if (routes.length === 0) return <Text variant="p">-</Text>;
    return <VStack className="gap-4 w-full">
        {routes.map((route, index) => <VStack key={`${idPrefix}-${index}`} className="gap-2">
            <MethodBadges methods={route.methods} getLabel={commutingMethodLabel} />
            {route.routeDetail && <Text variant="p">{route.routeDetail}</Text>}
            <HStack className="gap-6">
              {route.distance && <HStack className="gap-1 items-baseline">
                  <Text variant="labelSmallReg" className="text-neutral-muted-on-fill">
                    {`${t('distance')}:`}
                  </Text>
                  <Text variant="p">
                    {route.distance} {t('distanceUnit')}
                  </Text>
                </HStack>}
              {route.duration && Number(route.duration) > 0 && <HStack className="gap-1 items-baseline">
                  <Text variant="labelSmallReg" className="text-neutral-muted-on-fill">
                    {`${t('duration')}:`}
                  </Text>
                  <Text variant="p">
                    {route.duration} {t('durationUnit')}
                  </Text>
                </HStack>}
            </HStack>
            <FareDisplay fares={route.fares} getLabel={fareTypeLabel} />
            {index < routes.length - 1 && <Separator />}
          </VStack>)}
      </VStack>;
  };
  const renderWriteMode = () => {
    return <VStack className="gap-4 w-full">
        {routes.map((route, rIdx) => <VStack key={`${idPrefix}-w-${rIdx}`} className="gap-3 p-3 border border-neutral-muted-border rounded-md">
            <HStack className="justify-between items-center">
              <Text variant="labelSemiLarge">
                {t('route')} {rIdx + 1}
              </Text>
              <ActionButton icon="Trash2Icon" variant="ghost" iconColor="muted" onClick={() => handleRemoveRoute(rIdx)} disabled={props.readonly} aria-label={tc('action.delete')} />
            </HStack>

            {/* Methods */}
            <VStack className="gap-2">
              <Text variant="labelSmallReg" className="text-neutral-muted-on-fill">
                {t('commutingMethod.label')}
              </Text>
              {route.methods.map((m, mIdx) => <HStack key={`m-${mIdx}`} className="gap-2 items-center">
                  <SelectField value={m.commutingMethod ?? undefined} options={COMMUTING_METHOD_OPTIONS} placeholder={t('select')} onValueChange={v => handleMethodChange(rIdx, mIdx, v as CommutingMethod)} disabled={props.readonly} className="min-w-0" />
                  {route.methods.length > 1 && !props.readonly && <ActionButton icon="XIcon" variant="ghost" iconColor="muted" onClick={() => handleRemoveMethod(rIdx, mIdx)} aria-label={tc('action.delete')} />}
                </HStack>)}
              {!props.readonly && <ActionButton className="self-start pl-0" icon="PlusIcon" onClick={() => handleAddMethod(rIdx)} variant="ghost" iconColor="primary">
                  {t('addMethod')}
                </ActionButton>}
            </VStack>

            {/* Route detail */}
            <VStack className="gap-1">
              <Text variant="labelSmallReg" className="text-neutral-muted-on-fill">
                {t('routeDetail')}
              </Text>
              <Input width="100%" value={route.routeDetail ?? ''} onChange={e => handleRouteChange(rIdx, {
            routeDetail: e.target.value
          })} disabled={props.readonly} aria-label={t('routeDetail')} />
            </VStack>

            {/* Distance & Duration */}
            <HStack className="gap-4">
              <VStack className="gap-1 flex-1">
                <Text variant="labelSmallReg" className="text-neutral-muted-on-fill">
                  {`${t('distance')} (${t('distanceUnit')})`}
                </Text>
                <Input width="100%" type="text" inputMode="decimal" value={route.distance ?? ''} onChange={e => handleRouteChange(rIdx, {
              distance: e.target.value || null
            })} disabled={props.readonly} aria-label={t('distance')} />
              </VStack>
              <VStack className="gap-1 flex-1">
                <Text variant="labelSmallReg" className="text-neutral-muted-on-fill">
                  {`${t('duration')} (${t('durationUnit')})`}
                </Text>
                <Input width="100%" type="text" inputMode="numeric" value={route.duration ?? ''} onChange={e => handleRouteChange(rIdx, {
              duration: e.target.value || null
            })} disabled={props.readonly} aria-label={t('duration')} />
              </VStack>
            </HStack>

            {/* Fares */}
            <VStack className="gap-2">
              <Text variant="labelSmallReg" className="text-neutral-muted-on-fill">
                {t('fare')}
              </Text>
              {route.fares.map((f, fIdx) => <HStack key={`f-${fIdx}`} className="gap-2 items-end">
                  <VStack className="gap-1 shrink-0">
                    <SelectField value={f.fareType ?? undefined} options={FARE_TYPE_OPTIONS} placeholder={t('select')} onValueChange={v => handleFareChange(rIdx, fIdx, {
                fareType: v as FareType
              })} disabled={props.readonly} className="min-w-0" />
                  </VStack>
                  <VStack className="gap-1">
                    <Input width="100%" type="text" inputMode="numeric" value={f.fareAmount ?? ''} onChange={e => handleFareChange(rIdx, fIdx, {
                fareAmount: e.target.value || null
              })} disabled={props.readonly} aria-label={t('fare')} />
                  </VStack>
                  <VStack className="gap-1">
                    <SelectField value={f.currency ?? undefined} options={CURRENCY_OPTIONS} placeholder={t('currency.label')} onValueChange={v => handleFareChange(rIdx, fIdx, {
                currency: v as Currency
              })} disabled={props.readonly} className="min-w-0" />
                  </VStack>
                  {!props.readonly && <ActionButton icon="XIcon" variant="ghost" iconColor="muted" onClick={() => handleRemoveFare(rIdx, fIdx)} aria-label={tc('action.delete')} />}
                </HStack>)}
              {!props.readonly && <ActionButton className="self-start pl-0" icon="PlusIcon" onClick={() => handleAddFare(rIdx)} variant="ghost" iconColor="primary">
                  {t('addFare')}
                </ActionButton>}
            </VStack>
          </VStack>)}

        {!props.readonly && <ActionButton className="self-start pl-0" icon="PlusIcon" onClick={handleAddRoute} variant="ghost" iconColor="primary">
            {tc('action.add')}
          </ActionButton>}
      </VStack>;
  };
  return <CFormItemBase {...props} readComponent={renderReadMode()} writeComponent={renderWriteMode()} />;
});